import React, { useEffect, useRef, useCallback, useState } from 'react'
import { useHistory } from 'react-router'
import PropTypes from 'prop-types'
import AutoSizer from 'react-virtualized-auto-sizer'
import { isNumber } from 'lodash'
import { connect } from 'react-redux'
import { ListingCard, Footer, TutorialTooltip, hooks } from '@adc/ui-adc'
import classnames from 'classnames/bind'
import NearbyAssetTotals from '../assetTotals/nearbyTotals'
import AssetTotals from '../assetTotals'
import ListHeaders from '../listHeadersV2'
import ListingCardWrapper from '../listingCardWrapper/index'
import { getMaxResultsAllowed } from '../../util/maxResultsAllowed'
import { partitionNearbyAssets } from '../../util/nearbyCheckbox'
import SeoContent from '../seoContent/index'
import { getTargetRow, shouldDisplayAdglareCard } from '../../util/asset'
import { isMobileBreakpoint } from '../../util/osDetector'
import { updateMapMarkerSelectedId } from '../../actions/assets'
import {
  blueprintRemoveSelectedListingQueryParams,
  getTargetListingToScroll,
} from '../../util/searchUrl'
import BreadCrumbs from '../breadCrumbs'
import { DynamicSizeList, scrollToTop } from './dynamicSizeList'
import { CARD_WIDTH, GAP, MOBILE_WIDTH_BREAKPOINT } from './constants'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const LoadingState = () => {
  const cardListMapper = Array.from({ length: 12 }, () => null)
  return (
    <div className={cx('loading-property-card-container')}>
      {cardListMapper.map((_item, index) => (
        <ListingCard loading key={`loading-card-${index}`} />
      ))}
    </div>
  )
}

const BASE_ELM_ID = 'asset-list'
const VARIABLE_SIZE_LIST_REDRAW_DELAY = 10

const CardItem = cardItemProps => {
  const { listingToRender, cardProps, i, showAdContent = false } = cardItemProps

  let uniqueIdentifier = `asset_${listingToRender?.listing_id}`
  if (showAdContent) {
    uniqueIdentifier = `asset_inject_placeholder`
  }
  const { checkSaved, ...restCardProps } = cardProps

  const cardWrapper = (
    <ListingCardWrapper
      key={uniqueIdentifier}
      elmId={uniqueIdentifier}
      listing={listingToRender}
      isSaved={checkSaved(listingToRender)}
      position={i}
      showAdContent={showAdContent}
      {...restCardProps}
    />
  )
  return i === 0 ? (
    <TutorialTooltip
      tutorial={3}
      skip={isMobileBreakpoint()}
      position='bottom center'
      mobilePosition='bottom center'
      elmId='asset_row_tutorial'
      key={uniqueIdentifier}
      header='Browse Property Details'
      content='Review info such as address, pricing and auction date.'
    >
      {cardWrapper}
    </TutorialTooltip>
  ) : (
    cardWrapper
  )
}

const calculateItemsPerRow = width =>
  Math.max(Math.floor((width - GAP) / (CARD_WIDTH + GAP)), 1)

const ListingListComponent = ({
  assets,
  filters,
  checkSaved,
  featureFlags,
  toggleSaveAsset,
  updateApplicationFocusedListingId,
  updateSorting,
  updateFiltersAction,
  updateOpenedAsset,
  updateMapMarkerSelectedId,
}) => {
  const onMouseLeave = e => {
    e.stopPropagation()
    updateApplicationFocusedListingId(0)
  }

  const history = useHistory()

  const listRef = useRef(null)

  const [itemsPerRow, setItemsPerRow] = useState(0)
  const [focusedListingId, setFocusedListingId] = useState(null)

  const assetListString = JSON.stringify(assets.list)

  const prevSelectedId = hooks.usePrevious(
    assets.mapSelectedId,
    assets.mapSelectedId
  )

  useEffect(() => {
    if (listRef.current) {
      // we delay the update a bit
      // otherwise the redraw of the list rows will not always resolve correctly
      setTimeout(
        () => listRef.current?.resetAfterIndex(0),
        VARIABLE_SIZE_LIST_REDRAW_DELAY
      )
    }
  }, [assetListString, itemsPerRow])

  const { list: assetsList, fetchProgress, total } = assets

  const [exactMatchList, nearbyAssetsList] = partitionNearbyAssets(
    filters,
    assetsList
  )

  const verifiedImagesList = useRef({})

  const scrollToRow = useCallback(
    targetListingId => {
      setFocusedListingId(targetListingId)
      const targetRow = getTargetRow(
        targetListingId,
        exactMatchList,
        nearbyAssetsList,
        itemsPerRow
      )
      isNumber(targetRow) && listRef.current?.scrollToItem(targetRow, 'center')
    },
    [exactMatchList, nearbyAssetsList, itemsPerRow]
  )

  useEffect(() => {
    if (assets.mapSelectedId && assets.mapSelectedId !== prevSelectedId) {
      scrollToRow(assets.mapSelectedId)
    }
  }, [assets.mapSelectedId, scrollToRow, prevSelectedId])

  const prevFetchAssetsProgressRef = useRef(fetchProgress)

  const scrollToSelectedPosition = useCallback(() => {
    const listingIdToScrollTo = getTargetListingToScroll()
    if (listingIdToScrollTo?.length) {
      updateMapMarkerSelectedId(listingIdToScrollTo)
      history.push(blueprintRemoveSelectedListingQueryParams())
    } else {
      scrollToTop()
    }
  }, [updateMapMarkerSelectedId, history])

  useEffect(() => {
    if (
      prevFetchAssetsProgressRef.current?.loading === true &&
      fetchProgress?.loading === false
    ) {
      scrollToSelectedPosition()
    }
    prevFetchAssetsProgressRef.current = fetchProgress
  }, [fetchProgress, scrollToSelectedPosition])

  const isAdglareCardDisplayed = shouldDisplayAdglareCard(
    exactMatchList,
    featureFlags
  )

  // need to be careful about where it's set or what our source is depending on markers
  if (isAdglareCardDisplayed) {
    const adglarePlaceholderListing = {
      injectedContentConfiguration: assets.adglareContent
        ? { htmlOverride: assets.adglareContent }
        : undefined,
      injectPlaceholder: true,
    }

    exactMatchList.splice(
      featureFlags['fe.resi.srp-adglare-configuration']?.position - 1,
      0,
      adglarePlaceholderListing
    )
  }

  const shouldShowLoadingPlaceholder =
    Boolean(!exactMatchList.length && !nearbyAssetsList.length) ||
    fetchProgress.loading

  const getTotalCountToDisplay = (
    total,
    assetsListLength,
    maxResultsAllowed
  ) => {
    const maxResultsAllowedDisplayed = maxResultsAllowed.toLocaleString('en-US')
    const totalResultsAvailable = total.toLocaleString('en-US')
    const assetListTotal = assetsListLength.toLocaleString('en-US')
    const resultsDisplayed =
      total > maxResultsAllowed ? (
        <>
          <span data-elm-id={`${BASE_ELM_ID}_max_results_allowed`}>
            {maxResultsAllowedDisplayed}
          </span>
          {' of '}
          <span data-elm-id={`${BASE_ELM_ID}_total_results_available`}>
            {totalResultsAvailable}
          </span>
        </>
      ) : (
        <>
          <span data-elm-id={`${BASE_ELM_ID}_total_results`}>
            {assetListTotal}
          </span>{' '}
        </>
      )

    return (
      <div className={cx(`${BASE_ELM_ID}-footer-totals`)}>
        Showing {resultsDisplayed}
      </div>
    )
  }

  const updateVerifiedImage = (listingId, value) => {
    verifiedImagesList.current[listingId] = value
  }
  /**
   * Function to piece by piece construct the rows of the infinite list which comprises of the headers, sorting options, asset totals, actual listing cards to render, and the footer
   * @param {*} itemsPerRow the calculation of how many cards will fit in a row, requires the autosizer to determine the container size before we can use this to construct the actual list of rows
   * @param {*} rowCount the total assets to render divided by the itemsPerRow + the constant header/footer pieces
   * @param {*} cardProps card props sent to each card to allow it to function correctly
   * @returns
   */
  const getConstructedRows = (itemsPerRow, cardProps, width) => {
    const rows = []
    const headerRowHeight = (width < MOBILE_WIDTH_BREAKPOINT ? 180 : 162) - 55 // general headers bar will be 180 or 162 based on mobile width but if the notifyMeBar takes up 55 pixels so that is taken off the calculation if that doesn't show

    if (shouldShowLoadingPlaceholder) {
      rows.push({
        rowComponent: <LoadingState />,
        height: headerRowHeight,
      })

      return rows
      // TODO: we return here because if loading placeholder is showing, then any rows added afterwards could cause visual bugs as the loading placeholder height can exceed row size,
      // this is fixable if we want to run the calculation based off of width with the loading placeholder card count of 12 at the time of this comment
    } else {
      // Add general headers row
      rows.push({
        rowComponent: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-around',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            <ListHeaders
              elmId={`${BASE_ELM_ID}_headers`}
              filters={filters}
              featureFlags={featureFlags}
              updateSorting={updateSorting}
            />

            {!fetchProgress.loading && (
              <AssetTotals
                assets={assets}
                filters={filters}
                elmId={`${BASE_ELM_ID}_totals`}
                updateFiltersAction={updateFiltersAction}
              />
            )}
          </div>
        ),
        height: headerRowHeight,
      })
      // construct card rows for exact match list
      for (
        let rowIndex = 0;
        rowIndex < Math.ceil(exactMatchList.length / itemsPerRow);
        rowIndex++
      ) {
        const fromIndex = rowIndex * itemsPerRow
        const toIndex = Math.min(fromIndex + itemsPerRow, exactMatchList.length)

        const rowItems = []
        for (let i = fromIndex; i < toIndex; i++) {
          const listingToRender = exactMatchList[i]

          rowItems.push(
            CardItem({
              listingToRender,
              cardProps: {
                ...cardProps,
                isFocused: listingToRender.listing_id === focusedListingId,
                verifiedImage:
                  verifiedImagesList.current[listingToRender.listing_id],
              },
              i,
              showAdContent: listingToRender?.injectPlaceholder
                ? true
                : undefined,
            })
          )
        }

        rows.push({
          rowComponent: (
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              {rowItems}
            </div>
          ),
        })
      }

      // Add nearby match header row if applicable
      if (nearbyAssetsList.length > 0) {
        if (exactMatchList.length > 0) {
          rows.push({
            rowComponent: (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <NearbyAssetTotals
                  nearbyTotal={
                    total -
                    exactMatchList.length +
                    (isAdglareCardDisplayed ? 1 : 0)
                  }
                  filters={filters}
                  elmId='away_header'
                />
              </div>
            ),
            height: width < MOBILE_WIDTH_BREAKPOINT ? 50 : 62,
          })
        }

        for (
          let rowIndex = 0;
          rowIndex < Math.ceil(nearbyAssetsList.length / itemsPerRow);
          rowIndex++
        ) {
          const fromIndex = rowIndex * itemsPerRow
          const toIndex = Math.min(
            fromIndex + itemsPerRow,
            nearbyAssetsList.length
          )

          const nearbyRowItems = []
          for (let i = fromIndex; i < toIndex; i++) {
            const listingToRender = nearbyAssetsList[i]

            nearbyRowItems.push(
              CardItem({
                listingToRender,
                cardProps: {
                  ...cardProps,
                  isFocused: listingToRender.listing_id === focusedListingId,
                  verifiedImage:
                    verifiedImagesList.current[listingToRender.listing_id],
                },
                i: i + (exactMatchList.length || 0), // i is used to set data-position of the cards in the overall list for automation purposes, this prevents the i index to be reset to 0 when nearby assets are added
              })
            )
          }

          rows.push({
            rowComponent: (
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {nearbyRowItems}
              </div>
            ),
          })
        }
      }

      // Add footer row
      rows.push({
        rowComponent: (
          <>
            <BreadCrumbs filters={filters} />
            {getTotalCountToDisplay(
              total,
              assets.list.length,
              getMaxResultsAllowed(featureFlags)
            )}
            <div className={cx('footer-container')}>
              <hr className={cx('footer-separator')} />
              <SeoContent filters={filters} assets={assets} />
              <Footer />
            </div>
          </>
        ),
        height: 300,
      })
    }

    return rows
  }

  const onRowSizeUpdated = newSize => {
    if (newSize !== itemsPerRow) {
      setItemsPerRow(newSize)
    }
  }

  const onUpdateSelectedListingId = targetListingId => {
    setFocusedListingId(targetListingId)
    updateApplicationFocusedListingId(targetListingId)
  }

  return (
    <div data-elm-id={BASE_ELM_ID} className={cx(`${BASE_ELM_ID}`)}>
      <div
        onMouseLeave={onMouseLeave}
        data-elm-id={`${BASE_ELM_ID}_content_wrapper`}
        className={cx(`asset-list-content-wrapper`)}
      >
        <div
          data-elm-id={`${BASE_ELM_ID}_content_v2`}
          className={cx('property-card-container')}
        >
          <AutoSizer>
            {({ height, width }) => {
              const updatedItemsPerRow = calculateItemsPerRow(width)

              const cardProps = {
                checkSaved,
                featureFlags,
                toggleSaveAsset,
                updateSelectedId: onUpdateSelectedListingId,
                updateOpenedAsset,
                updateVerifiedImage,
              }

              const rows = getConstructedRows(
                updatedItemsPerRow,
                cardProps,
                width
              )

              return (
                <DynamicSizeList
                  listRef={listRef}
                  itemsPerRow={updatedItemsPerRow}
                  height={height}
                  width={width}
                  rows={rows}
                  onRowSizeUpdated={onRowSizeUpdated}
                  elmId={BASE_ELM_ID}
                />
              )
            }}
          </AutoSizer>
        </div>
      </div>
    </div>
  )
}

ListingListComponent.propTypes = {
  assets: PropTypes.object.isRequired,
  checkSaved: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  featureFlags: PropTypes.object.isRequired,
  toggleSaveAsset: PropTypes.func.isRequired,
  updateApplicationFocusedListingId: PropTypes.func.isRequired,
  updateSorting: PropTypes.func.isRequired,
  updateFiltersAction: PropTypes.func.isRequired,
  updateOpenedAsset: PropTypes.func.isRequired,
  updateMapMarkerSelectedId: PropTypes.func.isRequired,
}

const mapActions = {
  updateMapMarkerSelectedId,
}

export const ListingList = connect(null, mapActions)(ListingListComponent)
